import { useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import useCaregiver from "../../hooks/useCaregiver";
import useInstaPay from "../../hooks/useInstaPay";
import { useSocket } from "../../hooks/useSocket";
import { isLoading } from "../../Loading";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import ErrorScaffold from "../ErrorScaffold";
import LoadingScaffold from "../LoadingScaffold";
import InstPayScreen from "./InstaPayScreen";

interface Props {
  api: API;
  appState: AppState;
  onRequestNavigate: (page: Page) => void;
  onRequestGoToMain: () => void;
  onClickGoBack: () => void;
}

const InstaPayActivity = (props: Props) => {
  const caregiver = useCaregiver(props.appState);

  const instaPay = useInstaPay({
    api: props.api,
    onRequestGoToMain: props.onClickGoBack,
    onRequestNavigate: props.onRequestNavigate,
  });

  const overviewQuery = useQuery({
    queryKey: ["payroll", "overview"],
    queryFn: () => props.api.getPayrollOverview(),
    keepPreviousData: true,
  });

  const queryClient = useQueryClient();

  const visitsQuery = useQuery({
    queryKey: ["instapay", "history", [-1, 0]],
    queryFn: async () => {
      const prevWeek = await props.api.getPayrollInformation(-1);
      const currWeek = await props.api.getPayrollInformation(0);

      const visits = [prevWeek, currWeek].flatMap((x) => x.visits);

      queryClient.setQueryData(["instapay", "history", -1], prevWeek);
      queryClient.setQueryData(["instapay", "history", 0], currWeek);

      return visits;
    },
    select: (data) => {
      return data.filter((x) => {
        switch (x.status.type) {
          case "Paid":
          case "EligibleInstaPay":
          case "RegularPay":
            return false;
          case "InstaPayInProgress":
          case "MissingClockTimes":
          case "PendingIssues":
          case "TechnicalIssue":
          case "VisitNotCompleted":
            return true;
        }
      });
    },
    keepPreviousData: true,
  });

  useSocket({
    eventName: "InstapaySuccess",
    onEvent: () => {
      overviewQuery.refetch();
      visitsQuery.refetch();
    },
  });

  if (overviewQuery.isLoading || visitsQuery.isLoading || isLoading(caregiver)) {
    return (
      <LoadingScaffold title={translate(STRINGS.INSTAPAY)} onClickGoBack={props.onClickGoBack} />
    );
  }

  if (overviewQuery.isError || visitsQuery.isError) {
    return (
      <ErrorScaffold
        title={translate(STRINGS.OOPS)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickGoBack={props.onClickGoBack}
      />
    );
  }

  return (
    <InstPayScreen
      data={overviewQuery.data}
      caregiver={caregiver}
      visits={visitsQuery.data}
      onClickContinue={instaPay.withdraw}
      onRequestNavigate={props.onRequestNavigate}
      onClickGoBack={props.onClickGoBack}
    />
  );
};

export default InstaPayActivity;
